import type { FC, JSX } from 'react'
import { Fragment, useCallback } from 'react'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import type { ISiteRoom } from 'common/interfaces/ISiteRoom'

import { CartShopItem } from 'features/Cart/components/CartShopItem/CartShopItem'
import type { IOptionShopCartItem, IOptionShopCartOption } from 'features/Option'
import { OptionShopService } from 'features/Option'
import { useShopCartManager } from 'features/Option/hooks/useShopCartManager'
import { UPDATE_OPTION_SHOP_ITEMS } from 'features/Option/state/slices/optionSlice'

interface IProps {
  bookingId: string
  handleEdit: (option: ISiteRoom) => void
  options: IOptionShopCartOption[] | undefined
}

const {
  removeSlotFromShopItems,
  removeSlotsForDateFromShopItems,
  removeDayFromShopItems,
  removeEntireOptionFromShopItems,
  removeRoomFromShopItems,
} = OptionShopService

export const CartShopServiceList: FC<IProps> = ({ options, bookingId, handleEdit }) => {
  const { updateShopCartContent } = useShopCartManager()
  const dispatch = useAppDispatch()
  const { optionShopItems: shopItems } = useAppSelector((state) => state.optionReducer)

  const handleRemoveSlot = useCallback(
    (props: { optionId: number; roomId: number; slotId: number; day?: string }): void => {
      const updatedItems: IOptionShopCartItem[] = removeSlotFromShopItems({
        ...props,
        shopItems,
        bookingId,
      })

      dispatch(UPDATE_OPTION_SHOP_ITEMS(updatedItems))

      updateShopCartContent(updatedItems)
    },
    [shopItems],
  )

  const handleRemoveSlotsForDate = useCallback(
    (props: { optionId: number; date: string }): void => {
      const updatedItems: IOptionShopCartItem[] = removeSlotsForDateFromShopItems({
        ...props,
        shopItems,
        bookingId,
      })

      dispatch(UPDATE_OPTION_SHOP_ITEMS(updatedItems))

      updateShopCartContent(updatedItems)
    },
    [shopItems],
  )

  const handleRemoveEntireOption = useCallback(
    (props: { optionId: number }): void => {
      const updatedItems: IOptionShopCartItem[] = removeEntireOptionFromShopItems({
        ...props,
        shopItems,
        bookingId,
      })

      dispatch(UPDATE_OPTION_SHOP_ITEMS(updatedItems))

      updateShopCartContent(updatedItems)
    },
    [shopItems],
  )

  const handleRemoveRoom = useCallback(
    (props: { optionId: number; roomId: number }): void => {
      const updatedItems: IOptionShopCartItem[] = removeRoomFromShopItems({
        ...props,
        shopItems,
        bookingId,
      })

      dispatch(UPDATE_OPTION_SHOP_ITEMS(updatedItems))

      updateShopCartContent(updatedItems)
    },
    [shopItems],
  )

  const handleRemoveDay = useCallback(
    (props: { optionId: number; date: string }): void => {
      const updatedItems: IOptionShopCartItem[] = removeDayFromShopItems({
        ...props,
        shopItems,
        bookingId,
      })

      dispatch(UPDATE_OPTION_SHOP_ITEMS(updatedItems))

      updateShopCartContent(updatedItems)
    },
    [shopItems],
  )

  if (!options) {
    return null
  }

  return (
    <>
      {options.map(
        (shopOption: IOptionShopCartOption, index: number): React.JSX.Element => (
          <Fragment key={index}>
            <CartShopItem
              shopOption={shopOption}
              handleEdit={handleEdit}
              handleRemoveSlot={(roomId: number, slotId: number, day: string) =>
                handleRemoveSlot({ optionId: shopOption.option.id, roomId, slotId, day })
              }
              handleRemoveSlotsForDate={(date: string) =>
                handleRemoveSlotsForDate({ optionId: shopOption.option.id, date })
              }
              handleRemoveEntireOption={() =>
                handleRemoveEntireOption({ optionId: shopOption.option.id })
              }
              handleRemoveRoom={(roomId: number) =>
                handleRemoveRoom({ optionId: shopOption.option.id, roomId })
              }
              handleRemoveDay={(date: string) =>
                handleRemoveDay({ optionId: shopOption.option.id, date })
              }
            />
            <hr />
          </Fragment>
        ),
      )}
    </>
  )
}
